import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  createFamilyMember,
  deleteFamilyMember,
  type CreateFamilyMemberDto,
} from '@/api/familyMembers.api';
import { ApiError } from '@/api/client';

// ─── Kind-Eltern-Zuordnung erstellen ────────────────────────────────────────

/**
 * `blickrichtung` bestimmt nur die Erfolgsmeldung.
 *
 * Derselbe Datensatz lässt sich von beiden Seiten anlegen: vom Elternteil aus
 * („Kind hinzufügen") oder vom Kind aus („Eltern zuordnen"). Ohne diese
 * Unterscheidung meldete auch der zweite Weg „X als Kind zugeordnet" – richtig
 * im Datenmodell, verwirrend für jemanden, der gerade die Eltern eingetragen
 * hat.
 */
export function useCreateFamilyMember(blickrichtung: 'kind' | 'eltern' = 'kind') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateFamilyMemberDto) => createFamilyMember(dto),
    onSuccess: (fm) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.familyMembers.all });
      // Person-Detail des Kindes und der Eltern invalidieren
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(fm.childId) });
      if (fm.parent1Id) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(fm.parent1Id) });
      }
      if (fm.parent2Id) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(fm.parent2Id) });
      }

      const kindName = `${fm.child.firstName} ${fm.child.lastName ?? ''}`.trim();
      if (blickrichtung === 'eltern') {
        const eltern = [fm.parent1, fm.parent2]
          .filter(Boolean)
          .map((p) => `${p!.firstName} ${p!.lastName ?? ''}`.trim());
        toast.success(
          eltern.length > 0
            ? `${eltern.join(' und ')} als Eltern zugeordnet.`
            : 'Zuordnung gespeichert.',
        );
      } else {
        toast.success(`${kindName} als Kind zugeordnet.`);
      }
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Kind-Eltern-Zuordnung konnte nicht erstellt werden.');
    },
  });
}

// ─── Kind-Eltern-Zuordnung löschen ──────────────────────────────────────────

export function useDeleteFamilyMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteFamilyMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.familyMembers.all });
      toast.success('Kind-Eltern-Zuordnung entfernt.');
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Zuordnung konnte nicht entfernt werden.');
    },
  });
}
